<template lang="pug">
div
  h3.mb-4 Resumen de Asistencia
  
  .mb-4
    v-row
      v-col(cols="12", sm="6", md="3")
        v-select(
          v-model="selectedMonth",
          :items="months",
          label="Mes",
          outlined,
          dense,
          @change="fetchAttendance"
        )
      v-col(cols="12", sm="6", md="3")
        v-select(
          v-model="selectedYear",
          :items="years",
          label="Año",
          outlined,
          dense,
          @change="fetchAttendance"
        )
  
  v-overlay(:value="loading")
    v-progress-circular(indeterminate, size="64")
    
  v-alert(type="info", v-if="!loading && (!attendanceRecords || attendanceRecords.length === 0)" outlined)
    | No hay registros de asistencia para el período seleccionado.
  
  div(v-if="!loading && attendanceRecords && attendanceRecords.length > 0")
    // Tarjetas de resumen
    v-row
      v-col(cols="12", sm="6", md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Total Horas
            .text-h4 {{ monthlyTotal }}
      v-col(cols="12", sm="6", md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Días Trabajados
            .text-h4 {{ workDays }}
      v-col(cols="12", sm="6", md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Faltas
            .text-h4 {{ absenceDays }}
      v-col(cols="12", sm="6", md="3")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Llegadas Tarde
            .text-h4 {{ lateDays }}
  
    // Tabla de fichadas
    h3.mb-2.mt-6 Detalle de Asistencia
    v-data-table(
      :headers="attendanceHeaders",
      :items="attendanceRecords",
      :loading="loading",
      :items-per-page="5",
      class="elevation-1",
      no-data-text="No hay registros para el período seleccionado",
      :footer-props="{'items-per-page-options': [5, 10, 15]}"
    )
      template(v-slot:item.date="{ item }")
        | {{ formatDate(item.date) }}
      template(v-slot:item.checkIn="{ item }")
        | {{ formatTime(item.checkIn) }}
      template(v-slot:item.checkOut="{ item }")
        | {{ formatTime(item.checkOut) }}
      template(v-slot:item.totalHours="{ item }")
        | {{ item.totalHours ? item.totalHours.toFixed(2) : '-' }}
      template(v-slot:item.status="{ item }")
        v-chip(:color="getStatusColor(item.status)")
          | {{ getStatusLabel(item.status) }}
</template>

<script>
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { mapActions } from 'vuex'

export default {
  name: 'AttendanceSummary',
  
  props: {
    employeeId: {
      type: String,
      required: true
    }
  },
  
  data: () => ({
    loading: false,
    selectedMonth: new Date().getMonth() + 1,
    selectedYear: new Date().getFullYear(),
    attendanceRecords: [],
    monthlyTotal: '0',
    workDays: '0',
    absenceDays: '0',
    lateDays: '0',
    
    attendanceHeaders: [
      { text: 'Fecha', value: 'date' },
      { text: 'Entrada', value: 'checkIn' },
      { text: 'Salida', value: 'checkOut' },
      { text: 'Total Horas', value: 'totalHours' },
      { text: 'Estado', value: 'status' }
    ]
  }),
  
  computed: {
    months() {
      return [
        { text: 'Enero', value: 1 },
        { text: 'Febrero', value: 2 },
        { text: 'Marzo', value: 3 },
        { text: 'Abril', value: 4 },
        { text: 'Mayo', value: 5 },
        { text: 'Junio', value: 6 },
        { text: 'Julio', value: 7 },
        { text: 'Agosto', value: 8 },
        { text: 'Septiembre', value: 9 },
        { text: 'Octubre', value: 10 },
        { text: 'Noviembre', value: 11 },
        { text: 'Diciembre', value: 12 }
      ]
    },
    
    years() {
      const currentYear = new Date().getFullYear()
      const years = []
      
      for (let i = currentYear - 5; i <= currentYear; i++) {
        years.push(i)
      }
      
      return years
    }
  },
  
  methods: {
    ...mapActions({
      fetchMonthlyAttendance: 'employeeFiles/fetchMonthlyAttendance'
    }),
    
    formatDate(date) {
      if (!date) return '-'
      return format(new Date(date), 'dd/MM/yyyy', { locale: es })
    },
    
    formatTime(time) {
      if (!time) return '-'
      
      // Si es un string en formato ISO
      if (typeof time === 'string' && time.includes('T')) {
        return format(new Date(time), 'HH:mm')
      }
      
      // Si es un string en formato HH:mm:ss
      if (typeof time === 'string' && time.includes(':')) {
        return time.substring(0, 5) // Tomar sólo HH:mm
      }
      
      return time
    },
    
    getStatusColor(status) {
      const colors = {
        normal: 'success',
        late: 'warning',
        absent: 'error',
        excused: 'info'
      }
      return colors[status] || 'grey'
    },
    
    getStatusLabel(status) {
      const labels = {
        normal: 'Normal',
        late: 'Llegada Tarde',
        absent: 'Ausente',
        excused: 'Justificada',
        incomplete: 'Incompleta'
      }
      return labels[status] || status
    },
    
    async fetchAttendance() {
      this.loading = true
      
      try {
        // Usar la acción del store
        const data = await this.fetchMonthlyAttendance({ 
          employeeId: this.employeeId,
          year: this.selectedYear,
          month: this.selectedMonth
        })
        
        this.attendanceRecords = data.records || []
        this.monthlyTotal = data.summary?.totalHours?.toFixed(2) || '0'
        this.workDays = data.summary?.workDays || '0'
        this.absenceDays = data.summary?.absenceDays || '0'
        this.lateDays = data.summary?.lateDays || '0'
      } catch (error) {
        console.error('Error al cargar datos de asistencia:', error)
        this.$toast.error('Error al cargar datos de asistencia')
      } finally {
        this.loading = false
      }
    }
  },
  
  created() {
    this.fetchAttendance()
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
.v-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
}
</style>
